import { useRef } from 'react';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import type { OtaiLowestRatesQuery } from '../../overview-table/gql/_gen_/rate-shop.gql';
import type { DailyRatesData } from '../context/report-data-context';
import { RateDataExportButton } from './rate-data-table/rate-data-export-button';
import { RateTypeSelector } from './rate-data-table/rate-type-selector';
import { RateShopChart } from './rate-shop-chart';
import { RateShopTable, type RateShopTableRef } from './rate-shop-table';
import { useRateShopData } from './shared/hooks/use-rate-shop-data';
import { useRateShopFiltering } from './shared/hooks/use-rate-shop-filtering';

type RateDataTableProps = {
  brandCode: string;
  rateShopData?: OtaiLowestRatesQuery;
  lowestRates?: DailyRatesData;
  flexRates?: DailyRatesData;
  loading?: boolean;
  meetingCategory?: string;
};

export const RateDataTable = ({
  brandCode,
  rateShopData,
  lowestRates,
  flexRates,
  loading = false,
  meetingCategory,
}: RateDataTableProps) => {
  const rateShopTableRef = useRef<RateShopTableRef>(null);
  const { rateType, setRateType } = useRateShopFiltering();

  const { tableData, isEmpty } = useRateShopData({
    rateShopData,
    lowestRates,
    flexRates,
    rateType,
  });

  if (loading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2'></div>
          <p className='text-muted-foreground'>Loading rate data...</p>
        </div>
      </div>
    );
  }

  if (isEmpty) {
    return (
      <Alert>
        <AlertDescription>No rate data available for the selected filters.</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Chart Section - uses shared data */}
      <div className='h-[400px]'>
        <RateShopChart data={tableData} rateType={rateType} />
      </div>

      {/* Table Section with Filters */}
      <Card className='border-0 shadow-none'>
        <CardHeader>
          <div className='flex items-center justify-between'>
            <div>
              <CardTitle>Rate Shop Data</CardTitle>
              <CardDescription>90-day rate comparison across competitor hotels</CardDescription>
            </div>
            <div className='flex items-center gap-4'>
              <RateTypeSelector value={rateType} onValueChange={setRateType} />
              <RateDataExportButton
                brandCode={brandCode}
                data={tableData.rows}
                headers={tableData.headers}
                rateType={rateType}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Table uses same shared data */}
          <RateShopTable
            ref={rateShopTableRef}
            brandCode={brandCode}
            data={tableData}
            rateType={rateType}
            meetingCategory={meetingCategory}
          />
        </CardContent>
      </Card>
    </div>
  );
};
